import { Type } from "class-transformer";
import { PermissionEnum } from "../../../../common/constants/auth.constants";
import { IdentifiableContact } from "../crm/contracts/contact.contract";
import { UserRole } from "./user-role.model";

export class User extends IdentifiableContact {
  public get isCollaborationOnly():boolean {
    return !!this.userRoles?.length && this.userRoles.every(role=>role.enabled && Number(role.role?.id)===17);
  }
  uuid: string; // Unique identifier for the user to be passed around instead of externalId
  @Type(() => UserRole)
  userRoles: UserRole[];

  /**
   * @description Given 1 or more permissionIds, checks all the users roles to see if they have all the permissions.
   * @param permissionIds
   * @returns true\false
   */
  public hasPermissions(permissionIds: PermissionEnum[]): boolean {
    if (!this.userRoles || this.userRoles.length === 0) {
      return false;
    }
    return permissionIds.every((permissionId) =>
      this.userRoles.some((userRole) => userRole && userRole.hasPermission(permissionId))
    );
  }

  /**
   * @description Checks if the user has the Operations role
   */
  public get isOperations(): boolean {
    return this.userRoles.some((role) => {
      return role.isOperations;
    });
  }

  /**
   * @description Checks if the user has the client role
   */
  public get isClient(): boolean {
    return this.userRoles.some((role) => {
      return role.isClient;
    });
  }

  /**
   * @description Checks if the user has the practitioner role
   */
  public get isPractitioner(): boolean {
    return this.userRoles.some((role) => {
      return role.isPractitioner;
    });
  }

  /**
   * @description Checks if the user has the admin role
   */
  public get isAdmin(): boolean {
    return this.userRoles.some((role) => {
      return role.isAdmin;
    });
  }

  /**
   * @description Checks if the user has the dro role
   */
  public get isDRO(): boolean {
    return this.userRoles.some((role) => {
      return role.isDRO;
    });
  }

  public get isCorporateAdmin(): boolean {
    return this.userRoles.some((role) => {
      return role.isCorporateAdmin;
    });
  }
  public get isCollegeAdmin(): boolean {
    return this.userRoles.some((role) => {
      return role.isCollegeAdmin;
    });
  }

  public get isExternalAdmin(): boolean {
    return this.isCorporateAdmin || this.isCollegeAdmin || this.isKeyCompanyContact;
  }

  public get isKeyCompanyContact(): boolean {
    return this.userRoles.some((role) => {
      return role.isKeyCompanyContact;
    });
  }

  public get isCollegeAdminDelegate(): boolean {
    return this.userRoles.some((role) => {
      return role.isCollegeAdminDelegate;
    });
  }
  public get isCorporateAdminDelegate(): boolean {
    return this.userRoles.some((role) => {
      return role.isCorpAdminDelegate;
    });
  }
  public get isKeyCompanyContactDelegate(): boolean {
    return this.userRoles.some((role) => {
      return role.isKeyCompanyContactDelegate;
    });
  }

  public get isDelegate(): boolean {
    return (
      this.isCollegeAdminDelegate ||
      this.isCorporateAdminDelegate ||
      this.isKeyCompanyContactDelegate
    );
  }

  public get isInternalUser(): boolean {
    return this.isAdmin || this.isOperations || this.isDRO;
  }

  public get isRACGPUser(): boolean {
    return this.userRoles.some((role) => {
      return role.isRACGPUser;
    });
  }

  public getRolesAsString(): string {
    return this.userRoles.map((role) => role.name).join(", ");
  }
}
